import type { Anchor } from "@anota/shared";
import { LIMITS } from "@anota/shared/limits";
import { cssSelector, xPath } from "./selector";

/** Atributos que ayudan a encontrar el elemento en el código (más todos los data-* y aria-*). */
const USEFUL = new Set(["id", "class", "name", "type", "role", "href", "src", "alt", "title", "placeholder", "for", "action"]);

const squash = (text: string, max: number) => {
  const t = text.replace(/\s+/g, " ").trim();
  return t.length > max ? `${t.slice(0, max - 1)}…` : t;
};

/**
 * Un bloque grande (la página, una sección, un fondo, una imagen grande): ahí no se señala el bloque
 * entero sino el punto exacto del clic, con un pin. Grande = se ve más de un cuarto de la ventana.
 */
export function isPointTarget(el: Element): boolean {
  if (el === document.documentElement || el === document.body) return true;
  const r = el.getBoundingClientRect();
  const w = Math.min(r.right, innerWidth) - Math.max(r.left, 0);
  const h = Math.min(r.bottom, innerHeight) - Math.max(r.top, 0);
  return w > 0 && h > 0 && w * h > 0.25 * innerWidth * innerHeight;
}

/** Un rectángulo en coordenadas de la ventana (el área que se dibujó al elegir). */
export interface ViewportRect {
  left: number;
  top: number;
  width: number;
  height: number;
}

const clamp01 = (v: number) => Math.min(1, Math.max(0, v));

/**
 * El elemento más pequeño que contiene entera un área dibujada: el bloque del que trata la nota. Si no
 * hay ninguno (el área cruza varios), la página.
 */
export function containerOf(area: ViewportRect, ignore?: Element): Element {
  let el = document.elementFromPoint(area.left + area.width / 2, area.top + area.height / 2);
  if (el && ignore && (el === ignore || ignore.contains(el))) el = null;
  for (; el && el !== document.documentElement; el = el.parentElement) {
    const r = el.getBoundingClientRect();
    if (r.left <= area.left + 2 && r.top <= area.top + 2 && r.right >= area.left + area.width - 2 && r.bottom >= area.top + area.height - 2) return el;
  }
  return document.body ?? document.documentElement;
}

/** Todo lo que se guarda del elemento señalado (o del bloque de un área dibujada). Nunca el valor de un campo. */
export function describeElement(el: Element, click?: { x: number; y: number }, area?: ViewportRect): Anchor {
  const rect = el.getBoundingClientRect();
  const attributes: Record<string, string> = {};
  for (const attr of Array.from(el.attributes)) {
    if (Object.keys(attributes).length >= LIMITS.attributes) break;
    if (attr.name === "value" || attr.name === "style") continue;
    if (USEFUL.has(attr.name) || attr.name.startsWith("data-") || attr.name.startsWith("aria-")) attributes[attr.name] = attr.value.slice(0, 300);
  }

  const isField = el instanceof HTMLInputElement || el instanceof HTMLTextAreaElement || el instanceof HTMLSelectElement;
  const text = isField ? "" : squash((el as HTMLElement).innerText ?? el.textContent ?? "", LIMITS.text);
  // Solo la etiqueta (sin hijos ni el valor tecleado): basta para buscarla en el código.
  const shell = el.cloneNode(false) as Element;
  shell.removeAttribute("value");
  shell.removeAttribute("style");

  const source = el.closest("[data-anota-src]")?.getAttribute("data-anota-src") ?? undefined;
  // Un área: su recuadro en fracciones de la caja del bloque, y su centro como punto.
  const box =
    area && rect.width > 0 && rect.height > 0
      ? {
          x: clamp01((area.left - rect.left) / rect.width),
          y: clamp01((area.top - rect.top) / rect.height),
          width: clamp01(area.width / rect.width),
          height: clamp01(area.height / rect.height),
        }
      : undefined;
  if (box && area) click = { x: area.left + area.width / 2, y: area.top + area.height / 2 };
  const point =
    click && rect.width > 0 && rect.height > 0
      ? {
          x: Math.min(1, Math.max(0, (click.x - rect.left) / rect.width)),
          y: Math.min(1, Math.max(0, (click.y - rect.top) / rect.height)),
        }
      : undefined;

  return {
    selector: cssSelector(el).slice(0, LIMITS.selector),
    xpath: xPath(el).slice(0, LIMITS.selector),
    tag: el.tagName.toLowerCase().slice(0, 40),
    ...(text ? { text } : {}),
    ...(Object.keys(attributes).length ? { attributes } : {}),
    html: shell.outerHTML.slice(0, LIMITS.html),
    rect: {
      x: Math.round(rect.left + scrollX),
      y: Math.round(rect.top + scrollY),
      width: Math.round(rect.width),
      height: Math.round(rect.height),
    },
    ...(point ? { point } : {}),
    ...(box ? { kind: "area" as const, area: box } : point && isPointTarget(el) ? { kind: "point" as const } : {}),
    ...(source ? { source: source.slice(0, 300) } : {}),
  };
}
